import { BlobServiceClient, ContainerClient } from '@azure/storage-blob';

const getBlobServiceClient = (): BlobServiceClient => {
  const accountName = import.meta.env.VITE_AZURE_STORAGE_ACCOUNT_NAME;
  const sasToken = import.meta.env.VITE_AZURE_SAS_TOKEN;

  if (!accountName || !sasToken) {
    throw new Error(
      'Azure Storage account name or SAS token is not configured. Please check your .env file.'
    );
  }

  const blobServiceUri = `https://${accountName}.blob.core.windows.net`;
  return new BlobServiceClient(`${blobServiceUri}?${sasToken}`);
};

const getContainerClient = (): ContainerClient => {
  const containerName = import.meta.env.VITE_AZURE_CONTAINER_NAME;
  if (!containerName) {
    throw new Error(
      'Azure Storage container name is not configured. Please check your .env file.'
    );
  }
  const blobServiceClient = getBlobServiceClient();
  return blobServiceClient.getContainerClient(containerName);
};

/**
 * Replaces expired SAS token in Azure blob URLs with current environment SAS token
 * @param {string} originalUrl - The original Azure blob URL with expired SAS token
 * @returns {string} - Updated URL with current SAS token
 */
export const refreshAzureBlobUrl = (originalUrl: string): string => {
  if (!originalUrl || typeof originalUrl !== 'string') {
    return originalUrl;
  }

  return originalUrl;

  // TODO: Reenable if not using public object storage
  try {
    const accountName = import.meta.env.VITE_AZURE_STORAGE_ACCOUNT_NAME;
    const currentSasToken = import.meta.env.VITE_AZURE_SAS_TOKEN;

    if (!accountName || !currentSasToken) {
      console.warn('Azure configuration not found, returning original URL');
      return originalUrl;
    }

    // Check if this is an Azure blob URL
    const azureBlobPattern =
      /https:\/\/([^.]+)\.blob\.core\.windows\.net\/([^?]+)(\?.*)?/;
    const match = originalUrl.match(azureBlobPattern);

    if (!match) {
      // Not an Azure blob URL, return as-is
      return originalUrl;
    }

    const [, urlAccountName, blobPath] = match;

    // Verify this is the same storage account
    if (urlAccountName !== accountName) {
      console.warn(
        `URL is for different storage account (${urlAccountName} vs ${accountName}), returning original URL`
      );
      return originalUrl;
    }

    // Construct new URL with current SAS token
    const baseUrl = `https://${accountName}.blob.core.windows.net/${blobPath}`;
    const newUrl = `${baseUrl}?${currentSasToken}`;

    return newUrl;
  } catch (error) {
    console.error('Error refreshing Azure blob URL:', error);
    return originalUrl;
  }
};

/**
 * Uploads a file to Azure Blob Storage.
 * @param {File} file The file to upload.
 * @param {string} [blobName] The name for the blob. Defaults to the file's name.
 */
export const uploadFile = async (
  file: File,
  blobName?: string
): Promise<void> => {
  const containerClient = getContainerClient();
  const blockBlobClient = containerClient.getBlockBlobClient(
    blobName || file.name
  );
  await blockBlobClient.uploadData(file);
};

/**
 * Lists all blobs in the container.
 * @returns {Promise<string[]>} A promise that resolves to an array of blob names.
 */
export const listFiles = async (): Promise<string[]> => {
  const containerClient = getContainerClient();
  const blobs: string[] = [];
  for await (const blob of containerClient.listBlobsFlat()) {
    blobs.push(blob.name);
  }
  return blobs;
};

/**
 * Downloads a blob from Azure Blob Storage.
 * @param {string} blobName The name of the blob to download.
 * @returns {Promise<Blob | undefined>} A promise that resolves to the downloaded blob data.
 */
export const downloadFile = async (
  blobName: string
): Promise<Blob | undefined> => {
  const containerClient = getContainerClient();
  const blobClient = containerClient.getBlobClient(blobName);
  const downloadBlockBlobResponse = await blobClient.download();
  return await downloadBlockBlobResponse.blobBody;
};
